import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Search, Calendar, Film, Utensils, Check, X, Clock, Users, AlertTriangle, TrendingUp, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const deliverySummary = (delivery) => {
  if (!delivery || typeof delivery !== "object") return "—";
  const entries = Object.entries(delivery);
  if (entries.length === 0) return "—";
  const failed = entries.filter(([, value]) => String(value || "").startsWith("failed")).length;
  const sent = entries.filter(([, value]) => value === "sent").length;
  if (failed > 0) return `${failed} failed`;
  if (sent > 0) return `${sent} sent`;
  return "skipped";
};

export default function Bookings() {
  const { getAuthHeader, user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState("30");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const canViewAnalytics = ["admin", "manager"].includes(user?.role);

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    if (canViewAnalytics) {
      fetchAnalytics(analyticsDays);
    }
  }, [canViewAnalytics, analyticsDays]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bookings`, {
        headers: getAuthHeader()
      });
      setBookings(response.data);
    } catch (error) {
      toast.error("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async (days) => {
    try {
      const response = await axios.get(`${API_URL}/api/bookings/analytics/table`, {
        headers: getAuthHeader(),
        params: { days: Number(days) },
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to load table booking analytics");
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API_URL}/api/bookings/${bookingId}/status?status=${status}`, null, {
        headers: getAuthHeader()
      });
      toast.success(`Booking ${status}`);
      fetchBookings();
      if (canViewAnalytics) fetchAnalytics(analyticsDays);
    } catch (error) {
      toast.error("Failed to update booking");
    }
  };

  const exportTableReport = () => {
    const rows = restaurantBookings.map((booking) => ([
      booking.reference_id || "",
      booking.restaurant_name || "",
      booking.customer_name || "",
      booking.customer_phone || "",
      booking.booking_date || "",
      booking.booking_time || "",
      booking.guests || "",
      booking.status || "",
      booking.source || "",
      deliverySummary(booking.notification_delivery),
      booking.created_at || "",
    ]));
    const header = [
      "Reference",
      "Restaurant",
      "Customer",
      "Phone",
      "Date",
      "Time",
      "Guests",
      "Status",
      "Source",
      "Notifications",
      "Created",
    ];
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `table-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.reference_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.restaurant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.customer_phone?.includes(searchQuery);
    
    const matchesStatus = statusFilter === "all" || booking.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const movieBookings = filteredBookings.filter(b => b.booking_type === "movie");
  const restaurantBookings = filteredBookings.filter(b => b.booking_type === "restaurant");

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed": return "bg-emerald-100 text-emerald-700";
      case "pending": return "bg-amber-100 text-amber-700";
      case "cancelled": return "bg-red-100 text-red-700";
      case "completed": return "bg-blue-100 text-blue-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const BookingTable = ({ data, type }) => (
    <Table>
      <TableHeader>
        <TableRow className="table-header">
          <TableHead className="font-semibold">Reference</TableHead>
          <TableHead className="font-semibold">Customer</TableHead>
          <TableHead className="font-semibold">Date & Time</TableHead>
          <TableHead className="font-semibold">{type === "movie" ? "Seats" : "Guests"}</TableHead>
          <TableHead className="font-semibold">Status</TableHead>
          {type === "restaurant" ? <TableHead className="font-semibold">Notify</TableHead> : null}
          <TableHead className="font-semibold text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((booking) => (
          <TableRow key={booking.id} className="table-row" data-testid={`booking-row-${booking.id}`}>
            <TableCell className="font-mono text-sm">
              <div>
                <p>{booking.reference_id}</p>
                {type === "restaurant" && booking.restaurant_name ? (
                  <p className="text-xs text-muted-foreground">{booking.restaurant_name}</p>
                ) : null}
              </div>
            </TableCell>
            <TableCell>
              <div>
                <p className="font-medium">{booking.customer_name}</p>
                <p className="text-xs text-muted-foreground">{booking.customer_phone}</p>
              </div>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-mono text-sm">{booking.booking_date}</p>
                  <p className="text-xs text-muted-foreground">{booking.booking_time}</p>
                </div>
              </div>
            </TableCell>
            <TableCell className="font-mono">{type === "movie" ? booking.seats : booking.guests}</TableCell>
            <TableCell>
              <span className={`inline-flex px-2 py-1 text-xs font-medium ${getStatusColor(booking.status)}`}>
                {booking.status}
              </span>
            </TableCell>
            {type === "restaurant" ? (
              <TableCell className="text-xs text-muted-foreground">
                {deliverySummary(booking.notification_delivery)}
              </TableCell>
            ) : null}
            <TableCell className="text-right">
              <div className="flex justify-end gap-2">
                {booking.status === "pending" && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => updateBookingStatus(booking.id, "confirmed")}
                      className="text-emerald-600 hover:text-emerald-700"
                      title="Confirm"
                      data-testid={`confirm-booking-${booking.id}`}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => updateBookingStatus(booking.id, "cancelled")}
                      className="text-red-600 hover:text-red-700"
                      title="Cancel"
                      data-testid={`cancel-booking-${booking.id}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                )}
                {booking.status === "confirmed" && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => updateBookingStatus(booking.id, "completed")}
                    className="text-blue-600 hover:text-blue-700"
                    title="Mark Complete"
                    data-testid={`complete-booking-${booking.id}`}
                  >
                    <Clock className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bookings-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Bookings</h1>
          <p className="text-muted-foreground mt-1">Track requests, notifications, and restaurant table analytics</p>
        </div>
        {restaurantBookings.length > 0 ? (
          <Button variant="outline" className="rounded-none" onClick={exportTableReport} data-testid="export-table-bookings-btn">
            <Download className="h-4 w-4 mr-2" />
            Export table CSV
          </Button>
        ) : null}
      </div>

      {canViewAnalytics ? (
        <div className="space-y-4" data-testid="table-booking-analytics">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold font-['Manrope']">Table booking report</h2>
            <Select value={analyticsDays} onValueChange={setAnalyticsDays}>
              <SelectTrigger className="w-36 rounded-none" data-testid="analytics-days-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-border rounded-none">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Requests</p>
                <p className="text-2xl font-bold font-['Manrope'] mt-1">{analytics?.total || 0}</p>
              </CardContent>
            </Card>
            <Card className="border-border rounded-none">
              <CardContent className="p-4 flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold font-['Manrope'] mt-1">{analytics?.pending || 0}</p>
                </div>
                <Clock className="h-5 w-5 text-amber-600" />
              </CardContent>
            </Card>
            <Card className="border-border rounded-none">
              <CardContent className="p-4 flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Conversion</p>
                  <p className="text-2xl font-bold font-['Manrope'] mt-1">{analytics?.conversion_rate || 0}%</p>
                </div>
                <TrendingUp className="h-5 w-5 text-[#EC1A23]" />
              </CardContent>
            </Card>
            <Card className="border-border rounded-none">
              <CardContent className="p-4 flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Notify failures</p>
                  <p className="text-2xl font-bold font-['Manrope'] mt-1">{analytics?.notification_failures || 0}</p>
                </div>
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </CardContent>
            </Card>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-border rounded-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-['Manrope']">Daily volume</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics?.by_day || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E2E0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#EC1A23" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border rounded-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-['Manrope']">By restaurant</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {(analytics?.by_restaurant || []).length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">No table bookings in this window.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Restaurant</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Pending</TableHead>
                        <TableHead>Confirmed</TableHead>
                        <TableHead>Guests</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(analytics?.by_restaurant || []).slice(0, 8).map((row) => (
                        <TableRow key={row.restaurant_id || row.restaurant_name}>
                          <TableCell className="font-medium">{row.restaurant_name}</TableCell>
                          <TableCell>{row.total}</TableCell>
                          <TableCell>{row.pending}</TableCell>
                          <TableCell>{row.confirmed + row.completed}</TableCell>
                          <TableCell className="font-mono">{row.guests}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Users className="h-4 w-4" />{analytics?.guests_total || 0} guests requested</span>
            <span>{analytics?.restaurants_accepting || 0} restaurants accepting bookings</span>
            <span>Confirmed {analytics?.confirmed || 0} · Cancelled {analytics?.cancelled || 0} · Completed {analytics?.completed || 0}</span>
          </div>
        </div>
      ) : null}

      <Card className="border-border rounded-none">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by reference, restaurant, customer, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-none"
                data-testid="booking-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 rounded-none" data-testid="booking-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="restaurant" className="space-y-4">
        <TabsList className="rounded-none bg-muted p-1">
          <TabsTrigger value="all" className="rounded-none data-[state=active]:bg-white" data-testid="tab-all">
            All ({filteredBookings.length})
          </TabsTrigger>
          <TabsTrigger value="restaurant" className="rounded-none data-[state=active]:bg-white" data-testid="tab-restaurants">
            <Utensils className="h-4 w-4 mr-2" />
            Tables ({restaurantBookings.length})
          </TabsTrigger>
          <TabsTrigger value="movie" className="rounded-none data-[state=active]:bg-white" data-testid="tab-movies">
            <Film className="h-4 w-4 mr-2" />
            Movies ({movieBookings.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <Card className="border-border rounded-none">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="animate-pulse text-muted-foreground">Loading bookings...</div>
                </div>
              ) : filteredBookings.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                  <Calendar className="h-12 w-12 mb-3 opacity-50" />
                  <p>No bookings found</p>
                </div>
              ) : (
                <BookingTable data={filteredBookings} type="all" />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="movie">
          <Card className="border-border rounded-none">
            <CardContent className="p-0">
              {movieBookings.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                  <Film className="h-12 w-12 mb-3 opacity-50" />
                  <p>No movie bookings found</p>
                </div>
              ) : (
                <BookingTable data={movieBookings} type="movie" />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="restaurant">
          <Card className="border-border rounded-none">
            <CardContent className="p-0">
              {restaurantBookings.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                  <Utensils className="h-12 w-12 mb-3 opacity-50" />
                  <p>No restaurant bookings found</p>
                </div>
              ) : (
                <BookingTable data={restaurantBookings} type="restaurant" />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
